import { OutlinedInfoIcon, WhiteInfoIcon } from 'assets/icons';
import classnames from 'classnames';
import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { VoidFn } from 'types/baseTypes';
import { ButtonType, FormButtonType } from '../custom-button/CustomButton';

type TooltipPosition = 'top-left' | 'top-right' | 'custom';

interface ButtonWithTooltipProps {
  id: string;
  children?: ReactNode;
  StartIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string; id?: string }> | null;
  startIconId?: string;
  EndIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string }>;
  className?: string;
  type?: ButtonType;
  formType?: FormButtonType;
  disabled?: boolean;
  onClick?: VoidFn;
  childrenContainerStyles?: string;
  isLoading?: boolean;
  startIconStyle?: string;
  endIconStyle?: string;
  tooltipText: string;
  tooltipPosition?: TooltipPosition;
  customTooltipClassName?: string;
}

const ButtonWithTooltip: FC<ButtonWithTooltipProps> = ({
  id,
  children,
  StartIcon,
  startIconId,
  EndIcon,
  className,
  type = ButtonType.PRIMARY,
  onClick,
  formType = FormButtonType.button,
  childrenContainerStyles = '',
  isLoading = false,
  startIconStyle = '',
  endIconStyle = '',
  disabled = false,
  tooltipText,
  tooltipPosition = '',
  customTooltipClassName = ''
}) => {
  const isSecondaryBtn = type === ButtonType.SECONDARY;
  const isPrimaryBtn = type === ButtonType.PRIMARY;
  const isTertiaryBtn = type === ButtonType.TERTIARY;

  const getTooltipPositionClasses = () => {
    const baseArrowClasses =
      'before:border-4 before:border-x-transparent before:border-b-transparent before:border-t-white';

    if (tooltipPosition === 'top-left') {
      // Tooltip above, arrow points down from left
      return `-translate-x-[93%] left-0 -top-[44px] before:left-[93%] before:top-full ${baseArrowClasses}`;
    }

    if (tooltipPosition === 'top-right') {
      // Tooltip above, arrow points down from right
      return `translate-x-[93%] right-0 -top-[44px] before:right-[93%] before:top-full ${baseArrowClasses}`;
    }

    if (tooltipPosition === 'custom') {
      // Custom positioning via customTooltipClassName
      return `${customTooltipClassName} ${baseArrowClasses}`;
    }

    // default position (top center) - tooltip above, arrow points down from center
    return (
      'left-1/2 -translate-x-1/2 bottom-full mb-2 before:left-1/2 ' +
      `before:-translate-x-1/2 before:top-full ${baseArrowClasses}`
    );
  };

  const renderInfoIcon = () => {
    if (isSecondaryBtn) {
      return <OutlinedInfoIcon className="opacity-50" />;
    }
    if (isPrimaryBtn || isTertiaryBtn) {
      return <WhiteInfoIcon />;
    }
    return null;
  };

  return (
    <div className="group relative inline-block w-full">
      <button
        id={id}
        type={formType}
        onClick={onClick}
        className={twMerge(
          classnames(
            `flex h-11 w-fit cursor-pointer flex-row items-center 
             justify-center rounded-md px-[15px] text-sbase font-semibold
             active:shadow-xl disabled:cursor-not-allowed`,
            {
              'border border-secondary bg-primary text-secondaryBtnText shadow-sm hover:bg-[#ECF1F7]': isSecondaryBtn,
              'bg-[#9BBADD] text-primaryBtnText hover:bg-[#9BBADD]': isPrimaryBtn && disabled,
              'bg-primaryBtn text-primaryBtnText hover:bg-primaryButtonHover': isPrimaryBtn && !disabled,
              'bg-tertiaryText/50 text-primaryBtnText active:shadow-none': isTertiaryBtn && disabled,
              'bg-tertiaryText text-primaryBtnText active:shadow-none': isTertiaryBtn && !disabled
            }
          ),
          className
        )}
        disabled={disabled || isLoading}>
        {!isLoading && (
          <div className={twMerge('flex flex-row gap-2 text-sbase items-center', childrenContainerStyles)}>
            {Boolean(StartIcon) && <StartIcon className={twMerge('shrink-0', startIconStyle)} id={startIconId} />}
            <div>{children}</div>
            {Boolean(EndIcon) && <EndIcon className={twMerge('shrink-0', endIconStyle)} />}
            {disabled && tooltipText && (
              <div className="pointer-events-auto relative">
                {renderInfoIcon()}
                <div
                  className={twMerge(
                    'pointer-events-none absolute z-[100] hidden whitespace-nowrap rounded border',
                    'bg-white px-3 py-2 text-[13px] text-primaryText shadow-lg',
                    'before:absolute group-hover:block',
                    getTooltipPositionClasses()
                  )}
                  role="tooltip"
                  aria-hidden="true">
                  {tooltipText}
                </div>
              </div>
            )}
          </div>
        )}
      </button>
    </div>
  );
};

export default ButtonWithTooltip;
